import type {
  SponsoredDisplayCapabilities,
  SponsoredDisplayOptimizationGoal,
} from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import type { SponsoredDisplayDraft, UpdateDraftField } from "./sponsored-display-draft";

export function AdGroupFields({
  draft,
  sponsoredDisplayCapabilities,
  onUpdate,
}: {
  draft: SponsoredDisplayDraft;
  sponsoredDisplayCapabilities: SponsoredDisplayCapabilities;
  onUpdate: UpdateDraftField;
}) {
  const { t } = useTranslation("campaignCreation");
  const goals: SponsoredDisplayOptimizationGoal[] = [
    "page_visits",
    "conversions",
    "viewable_impressions",
  ];
  const audienceSupported = [
    sponsoredDisplayCapabilities.targeting.amazonAudience,
    sponsoredDisplayCapabilities.targeting.viewsRemarketing,
    sponsoredDisplayCapabilities.targeting.purchasesRemarketing,
  ].some((capability) => capability.state === "supported");
  const modeSupported = {
    audience: audienceSupported,
    contextual: sponsoredDisplayCapabilities.targeting.contextual.state === "supported",
  };
  const goalSupported = {
    page_visits: sponsoredDisplayCapabilities.optimization.pageVisits.state === "supported",
    conversions: sponsoredDisplayCapabilities.optimization.conversions.state === "supported",
    viewable_impressions:
      sponsoredDisplayCapabilities.optimization.viewableImpressions.state === "supported",
  };
  return (
    <FieldGroup>
      <Field className="max-w-[350px]">
        <FieldLabel htmlFor="sd-ad-group-name">{t("sd.adGroup.name")} *</FieldLabel>
        <Input
          id="sd-ad-group-name"
          value={draft.adGroupName}
          onChange={(event) => onUpdate("adGroupName", event.target.value)}
          maxLength={128}
        />
      </Field>
      <FieldSet>
        <FieldLegend>{t("sd.adGroup.targetingType")}</FieldLegend>
        <RadioGroup
          className="grid grid-cols-2 gap-3"
          value={draft.targetingMode}
          onValueChange={(value) => {
            if (value === "audience" || value === "contextual") onUpdate("targetingMode", value);
          }}
        >
          {(["audience", "contextual"] as const).map((mode) => (
            <label
              htmlFor={`sd-targeting-${mode}`}
              className={cn(
                "flex cursor-pointer gap-3 rounded-lg border border-line p-4",
                draft.targetingMode === mode && "border-primary bg-primary/5",
              )}
              key={mode}
            >
              <Radio id={`sd-targeting-${mode}`} value={mode} disabled={!modeSupported[mode]} />
              <span>
                <span className="block font-medium">{t(`sd.adGroup.targeting.${mode}.title`)}</span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  {t(`sd.adGroup.targeting.${mode}.description`)}
                </span>
                {!modeSupported[mode] ? (
                  <Badge className="mt-2" variant="outline">
                    {t("sd.unsupported")}
                  </Badge>
                ) : null}
              </span>
            </label>
          ))}
        </RadioGroup>
      </FieldSet>
      <FieldSet>
        <FieldLegend>{t("sd.adGroup.bidOptimization")}</FieldLegend>
        <RadioGroup
          className="grid grid-cols-3 gap-3"
          value={draft.optimizationGoal}
          onValueChange={(value) => {
            if (
              value === "page_visits" ||
              value === "conversions" ||
              value === "viewable_impressions"
            ) {
              onUpdate("optimizationGoal", value);
            }
          }}
        >
          {goals.map((goal) => (
            <label
              htmlFor={`sd-optimization-${goal}`}
              className={cn(
                "flex cursor-pointer gap-3 rounded-lg border border-line p-4",
                draft.optimizationGoal === goal && "border-primary bg-primary/5",
              )}
              key={goal}
            >
              <Radio id={`sd-optimization-${goal}`} value={goal} disabled={!goalSupported[goal]} />
              <span>
                <span className="block font-medium">
                  {t(`sd.adGroup.optimization.${goal}.title`)}
                </span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  {t(`sd.adGroup.optimization.${goal}.description`)}
                </span>
                {!goalSupported[goal] ? (
                  <Badge className="mt-2" variant="outline">
                    {t("sd.unsupported")}
                  </Badge>
                ) : null}
              </span>
            </label>
          ))}
        </RadioGroup>
      </FieldSet>
    </FieldGroup>
  );
}
